import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

interface PreloaderProps {
    onComplete: () => void;
}

const Preloader: React.FC<PreloaderProps> = ({ onComplete }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLDivElement>(null);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        // Simulate loading
        const interval = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) {
                    clearInterval(interval);
                    return 100;
                }
                return Math.min(prev + Math.random() * 8, 100);
            });
        }, 100);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let width = window.innerWidth;
        let height = window.innerHeight;
        canvas.width = width;
        canvas.height = height;

        const stars: { x: number; y: number; z: number; o: number }[] = [];
        const STAR_COUNT = 1000;
        let speed = 2; // Initial speed
        let warpFactor = 0; // Stretch factor

        // Initialize stars
        for (let i = 0; i < STAR_COUNT; i++) {
            stars.push({
                x: Math.random() * width - width / 2,
                y: Math.random() * height - height / 2,
                z: Math.random() * width,
                o: Math.random()
            });
        }

        let animationId: number;
        let isWarping = false;

        const draw = () => {
            // Clear with trail effect during warp
            ctx.fillStyle = isWarping ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 1)';
            ctx.fillRect(0, 0, width, height);

            const cx = width / 2;
            const cy = height / 2;

            stars.forEach((star) => {
                // Move star closer
                star.z -= speed;

                // Reset if passed camera
                if (star.z <= 0) {
                    star.x = Math.random() * width - width / 2;
                    star.y = Math.random() * height - height / 2;
                    star.z = width;
                }

                // Project 3D to 2D
                const x = cx + (star.x / star.z) * width;
                const y = cy + (star.y / star.z) * height;

                // Size based on depth
                const size = Math.max(0, (1 - star.z / width) * 2.5);
                const opacity = (1 - star.z / width);

                // Skip if star is too far (negative size/opacity would result)
                if (size <= 0) return;

                if (x > 0 && x < width && y > 0 && y < height) {
                    ctx.beginPath();
                    ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;

                    if (isWarping) {
                        // Draw lines (Warp Effect)
                        const xPrev = cx + (star.x / (star.z + speed * 2)) * width;
                        const yPrev = cy + (star.y / (star.z + speed * 2)) * height;

                        ctx.lineWidth = size;
                        ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`;
                        ctx.moveTo(x, y);
                        ctx.lineTo(xPrev, yPrev);
                        ctx.stroke();
                    } else {
                        // Draw dots (Regular)
                        ctx.arc(x, y, size, 0, Math.PI * 2);
                        ctx.fill();
                    }
                }
            });

            animationId = requestAnimationFrame(draw);
        };

        draw();

        const handleResize = () => {
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = width;
            canvas.height = height;
        };
        window.addEventListener('resize', handleResize);

        // Trigger Warp on 100%
        if (progress >= 100 && !isWarping) {
            isWarping = true;

            // GSAP SEQUENCE
            const tl = gsap.timeline({
                onComplete: onComplete
            });

            // 1. Hide Text
            tl.to(textRef.current, { opacity: 0, duration: 0.2 });

            // 2. Accelerate stars (handled in loop via 'speed' logic update below)
            // We'll use a dummy object to tween the speed variable
            const speedObj = { val: 2 };
            tl.to(speedObj, {
                val: 80, // Massive speed increase
                duration: 2,
                ease: "expo.in",
                onUpdate: () => { speed = speedObj.val; } // Update the render loop speed
            });

            // 3. Flash White
            tl.to(containerRef.current, {
                backgroundColor: '#ffffff',
                duration: 0.1,
                ease: "power1.out"
            });

            // 4. Fade Out
            tl.to(containerRef.current, {
                opacity: 0,
                duration: 0.5,
            });
        } else {
            // Moderate speed increase based on progress (0-100 -> speed 2-10)
            speed = 2 + (progress / 100) * 8;
        }

        return () => {
            cancelAnimationFrame(animationId);
            window.removeEventListener('resize', handleResize);
        };
    }, [progress, onComplete]);

    return (
        <div
            ref={containerRef}
            className="fixed inset-0 bg-black z-[100] flex items-center justify-center overflow-hidden"
        >
            <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

            {/* Progress Text */}
            <div
                ref={textRef}
                className="absolute z-10 flex flex-col items-center gap-2 mix-blend-difference"
            >
                <span className="text-4xl md:text-6xl font-bold text-white font-['Outfit'] tracking-tighter">
                    {Math.floor(progress)}%
                </span>
                <span className="text-xs text-orange-200/50 font-mono tracking-[0.3em] uppercase">
                    Hyperdrive Charging
                </span>
            </div>
        </div>
    );
};

export default Preloader;
